// src/features/pois/PeopleVisitsPanel.jsx
//
// People tab of a POI's Visit History: staff who dwelt at the POI (their
// phone positions) and the Work stops completed there, with the form entries
// submitted at each stop — a chip opens the Submissions page's slider.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ClipboardCheck, ChevronRight, MapPinCheck, Footprints } from 'lucide-react';
import dayjs from 'dayjs';
import { request } from '@/lib/trackizy';
import SubmissionSlider from '@/features/forms/SubmissionSlider';
import { peopleTimeline, peopleOptions } from './peopleVisits';

function formatDwell(ms) {
  const minutes = Math.round((ms || 0) / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export default function PeopleVisitsPanel({ poi, dateRange, sortOrder, sortButton }) {
  const { t } = useTranslation();
  const [data, setData] = useState({ key: null, visits: [], stops: [] });
  const [userId, setUserId] = useState('all');
  const [openSubmissionId, setOpenSubmissionId] = useState(null);
  const key = `${poi.id}|${dateRange.from}|${dateRange.to}`;

  useEffect(() => {
    let cancelled = false;
    request(`/pois/${poi.id}/staff-visits?from=${encodeURIComponent(dateRange.from)}&to=${encodeURIComponent(dateRange.to)}`)
      .then((res) => { if (!cancelled) setData({ key, visits: res?.visits || [], stops: res?.stops || [] }); })
      .catch(() => { if (!cancelled) setData({ key, visits: [], stops: [], error: true }); });
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const loading = data.key !== key;
  const options = useMemo(() => peopleOptions(data), [data]);
  const rows = useMemo(() => peopleTimeline(data, { userId, order: sortOrder }), [data, userId, sortOrder]);

  return (
    <>
      <div className="px-4 py-2 border-b border-slate-50 dark:border-slate-800 flex gap-2">
        <select
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          disabled={options.length === 0}
          className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg text-xs bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-50"
        >
          <option value="all">{t('pois.allPeople', 'All people')}</option>
          {options.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        {sortButton}
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={20} className="animate-spin text-slate-300" />
          </div>
        ) : data.error ? (
          <div className="text-center py-12 text-slate-400 text-xs">{t('pois.peopleLoadError', 'Could not load people visits')}</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 text-slate-400 text-xs">{t('pois.noPeopleVisits', 'No people visits or completed stops in this period')}</div>
        ) : (
          <div className="divide-y divide-slate-50 dark:divide-slate-800">
            {rows.map((row) => (row.type === 'visit' ? (
              <div key={row.key} className="px-4 py-3 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                    <Footprints size={12} className="text-slate-400" /><bdi>{row.userName || '—'}</bdi>
                  </span>
                  <span className="text-slate-400">{dayjs(row.at).format('MMM D')}</span>
                </div>
                <div className="flex items-center gap-4 text-slate-500">
                  <span>{t('pois.arrived')}: {dayjs(row.visit.arrivedAt).format('HH:mm')}</span>
                  <span>{t('pois.departed')}: {dayjs(row.visit.departedAt).format('HH:mm')}</span>
                </div>
                <div className="mt-1 text-slate-600 dark:text-slate-400 font-medium">
                  {t('pois.duration')}: {formatDwell(row.visit.dwellMs)}
                </div>
              </div>
            ) : (
              <div key={row.key} className="px-4 py-3 text-xs bg-emerald-50/40 dark:bg-emerald-900/10">
                <div className="flex items-center justify-between mb-1 gap-2">
                  <span className="font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5 min-w-0">
                    <MapPinCheck size={12} className="shrink-0 text-emerald-600" />
                    <bdi className="truncate">{row.stop.title}</bdi>
                  </span>
                  <span className="shrink-0 text-slate-400">{dayjs(row.at).format('MMM D')}</span>
                </div>
                <div className="text-slate-500 truncate">
                  {t('pois.stopOfWork', 'Work')}: <bdi>{row.stop.work_title || '—'}</bdi>
                </div>
                <div className="flex items-center gap-4 text-slate-500 mt-0.5">
                  <span><bdi>{row.userName || t('work.assignee.unassigned', 'Unassigned')}</bdi></span>
                  <span>{t('pois.stopCompleted', 'Completed')}: {dayjs(row.stop.completed_at).format('HH:mm')}</span>
                </div>
                {row.stop.submissions.length > 0 ? (
                  <div className="flex flex-wrap">
                    {row.stop.submissions.map((sub) => (
                      <button
                        key={sub.id}
                        type="button"
                        onClick={() => setOpenSubmissionId(sub.id)}
                        className="mt-1 me-1 inline-flex max-w-full items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold bg-sky-100 text-sky-700 hover:bg-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:hover:bg-sky-900/50"
                      >
                        <ClipboardCheck size={10} className="shrink-0" />
                        <bdi className="truncate">{sub.form_name}</bdi>
                        <ChevronRight size={10} className="shrink-0 rtl:rotate-180" />
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            )))}
          </div>
        )}
      </div>

      {openSubmissionId ? (
        <SubmissionSlider submissionId={openSubmissionId} onClose={() => setOpenSubmissionId(null)} />
      ) : null}
    </>
  );
}
